import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import PublicLayout from '../components/PublicLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Send, ExternalLink, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const Contact = () => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t('contact_required_fields'));
      return;
    }
    setSending(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      toast.success(t('contact_success_toast'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('contact_error_toast'));
    } finally {
      setSending(false);
    }
  };

  // Builds a mailto fallback with the form fields pre-filled
  const mailtoHref = () => {
    const subject = encodeURIComponent(form.subject || t('contact_default_subject'));
    const body = encodeURIComponent(
      `${t('contact_field_name')}: ${form.name}\n${t('contact_field_email')}: ${form.email}\n\n${form.message}`
    );
    return `mailto:asistencia.teko@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <p className="text-[11px] uppercase tracking-[0.25em] mb-2" style={{ color: theme.textMuted }}>
            {t('contact_kicker')}
          </p>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-4" style={{ color: theme.accent }}>
            {t('contact_title')}
          </h1>
          <p className="text-sm max-w-xl mx-auto" style={{ color: theme.textSecondary }}>
            {t('contact_subtitle')}
          </p>
        </div>

        {/* Quick email card */}
        <div className="rounded-2xl p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${theme.accent}20`, border: `1px solid ${theme.accent}40` }}>
            <Mail size={18} style={{ color: theme.accent }} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: theme.textMuted }}>
              {t('contact_email_label')}
            </p>
            <a href="mailto:asistencia.teko@gmail.com" data-testid="contact-mailto-direct"
              className="text-lg font-medium" style={{ color: theme.accent }}>
              asistencia.teko@gmail.com
            </a>
          </div>
        </div>

        {/* Form */}
        {sent ? (
          <div className="rounded-2xl p-10 text-center"
            style={{ background: theme.card, border: `1px solid ${theme.success}50` }}
            data-testid="contact-sent-confirmation">
            <CheckCircle2 size={40} className="mx-auto mb-4" style={{ color: theme.success }} />
            <h2 className="text-2xl font-light tracking-tight mb-2" style={{ color: theme.textPrimary }}>
              {t('contact_sent_title')}
            </h2>
            <p className="text-sm mb-6" style={{ color: theme.textSecondary }}>
              {t('contact_sent_body_pre')} <strong style={{ color: theme.textPrimary }}>{form.email}</strong> {t('contact_sent_body_post')}
            </p>
            <button onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
              className="text-sm underline" style={{ color: theme.accent }}>
              {t('contact_send_another')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl p-6 sm:p-8 space-y-5"
            style={{ background: theme.card, border: `1px solid ${theme.border}` }}
            data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: theme.textMuted }}>{t('contact_field_name')} *</label>
                <Input value={form.name} onChange={(e) => update('name', e.target.value)}
                  required maxLength={120}
                  style={{ background: theme.elevated, color: theme.textPrimary, borderColor: theme.border }}
                  data-testid="contact-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: theme.textMuted }}>{t('contact_field_email')} *</label>
                <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                  required maxLength={120}
                  style={{ background: theme.elevated, color: theme.textPrimary, borderColor: theme.border }}
                  data-testid="contact-email" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: theme.textMuted }}>{t('contact_field_subject')}</label>
              <Input value={form.subject} onChange={(e) => update('subject', e.target.value)}
                placeholder={t('contact_subject_placeholder')} maxLength={200}
                style={{ background: theme.elevated, color: theme.textPrimary, borderColor: theme.border }}
                data-testid="contact-subject" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: theme.textMuted }}>{t('contact_field_message')} *</label>
              <Textarea value={form.message} onChange={(e) => update('message', e.target.value)}
                required rows={6} maxLength={4000}
                placeholder={t('contact_message_placeholder')}
                style={{ background: theme.elevated, color: theme.textPrimary, borderColor: theme.border }}
                data-testid="contact-message" />
              <p className="text-[10px] mt-1 text-right" style={{ color: theme.textMuted }}>
                {form.message.length} / 4000
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between">
              <a href={mailtoHref()} data-testid="contact-mailto-fallback"
                className="inline-flex items-center justify-center gap-2 text-sm px-5 py-2.5 rounded-md"
                style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}` }}>
                <ExternalLink size={14} /> {t('open_email')}
              </a>
              <Button type="submit" disabled={sending} data-testid="contact-submit"
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 font-medium"
                style={{ background: theme.accent, color: theme.bg }}>
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? t('sending') : t('contact_send')}
              </Button>
            </div>
            <p className="text-[11px] text-center" style={{ color: theme.textMuted }}>
              {t('contact_consent')}{' '}
              <a href="/privacidad" style={{ color: theme.accent }}>{t('contact_consent_link')}</a>.
            </p>
          </form>
        )}
      </div>
    </PublicLayout>
  );
};

export default Contact;
